import React, { useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { TextField, Grid } from '@mui/material'

import { Logger, LoggerTypes } from '../../../../utils'
import { scopeTypes, textFieldTypes, variantTypes } from '../../../../constants'
import { iconNames } from '../../../icons'
import { FormTitle } from '../../../forms'

const defaultValue = {
  id: null,
  name: null,
  description: null,
  quantity: '0',
  weight: null,
  dimensions: null
}

/**
 *
 */
export const AttributeProductForm = forwardRef(({ setOpenDialog, title, product, dispatchAction, errors }, ref) => {
  const { t } = useTranslation()

  const [id] = useState(product.id || defaultValue.id)
  const [name, setName] = useState(product.name || defaultValue.name)
  const [description, setDescription] = useState(product.description || defaultValue.description)
  const [quantity, setQuantity] = useState(product.quantity || defaultValue.quantity)
  const [weight, setWeight] = useState(product.weight || defaultValue.weight)
  const [dimensions, setDimensions] = useState(product.dimensions || defaultValue.dimensions)

  const onChangeName = (e) => setName(e.target.value)
  const onChangeDescription = (e) => setDescription(e.target.value)
  const onChangeQuantity = (e) => setQuantity(e.target.value)
  const onChangeWeight = (e) => setWeight(e.target.value)
  const onChangeDimensions = (e) => setDimensions(e.target.value)

  const isDisabled = product?.scope === scopeTypes.IMPORT

  const onCloseDialog = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'AttributeProductForm.onCloseDialog()')
    setOpenDialog(false)
  }

  useImperativeHandle(ref, () => ({
    onSubmitForm() {
      const newProduct = {
        name: name === '' ? null : name,
        description: description === '' ? null : description,
        quantity: quantity === '' ? null : quantity,
        weight: weight === '' ? null : weight,
        dimensions: dimensions === '' ? null : dimensions
      }

      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeProductForm.onSubmitForm()', {
        newProduct,
        product
      })

      const _onSuccess = (res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeProductForm.onSubmitForm()._onSuccess()', { res })

        // -- close popin only in case of success
        onCloseDialog()
      }

      dispatchAction(newProduct, id, _onSuccess)
    }
  }))

  Logger.trace(LoggerTypes.CONTAINER, 'AttributeProductForm.render()', {
    product: {
      name,
      description,
      quantity,
      dimensions,
      weight
    },
    errors
  })

  return (
    <div className="AttributeForm attributes-resource-form">
      <FormTitle title={title} icon={iconNames.PRODUCT} iconSize={22} />
      {isDisabled ? <div className="importGlobal">{t('global.title')}</div> : ''}
      <form>
        <Grid container={true} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2} style={{ float: 'left' }}>
              {/*  name  */}
              <Grid item xs={12} sm={12}>
                <TextField
                  type={textFieldTypes.TEXT}
                  name="name"
                  id="name"
                  value={name}
                  disabled={isDisabled}
                  label={t('attributes.products.name.label')}
                  className="common_fullwidth"
                  onChange={onChangeName}
                  placeholder={t('attributes.products.name.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.name)}
                  helperText={(errors && errors?.name) || ''}
                />
              </Grid>

              {/*  quantity  */}
              <Grid item xs={12} sm={12}>
                <TextField
                  type={textFieldTypes.NUMBER}
                  name="quantity"
                  id="quantity"
                  value={quantity}
                  label={t('attributes.products.quantity.label')}
                  onChange={onChangeQuantity}
                  className="common_fullwidth"
                  placeholder={t('attributes.products.quantity.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.quantity)}
                  helperText={(errors && errors?.quantity) || ''}
                />
              </Grid>

              {/*  weight  */}
              <Grid item xs={12} sm={12}>
                <TextField
                  type={textFieldTypes.NUMBER}
                  name="weight"
                  id="weight"
                  value={weight}
                  disabled={isDisabled}
                  label={t('attributes.products.weight.label')}
                  onChange={onChangeWeight}
                  className="common_fullwidth"
                  placeholder={t('attributes.products.weight.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.weight)}
                  helperText={(errors && errors?.weight) || ''}
                />
              </Grid>

              {/*  dimensions  */}
              <Grid item xs={12} sm={12}>
                <TextField
                  type={textFieldTypes.TEXT}
                  name="dimensions"
                  id="dimensions"
                  value={dimensions}
                  disabled={isDisabled}
                  label={t('attributes.products.dimensions.label')}
                  onChange={onChangeDimensions}
                  className="common_fullwidth"
                  placeholder={t('attributes.products.dimensions.placeholder')}
                  variant={variantTypes.OUTLINED}
                  error={Boolean(errors && errors?.dimensions)}
                  helperText={(errors && errors?.dimensions) || ''}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* description */}
            <Grid item sm={12} xs={12} className="description-container">
              <TextField
                type={textFieldTypes.TEXT}
                multiline={true}
                rows={10.4}
                name="description"
                id="description"
                disabled={isDisabled}
                label={t('attributes.products.description.label')}
                onChange={onChangeDescription}
                value={description}
                className="common_fullwidth"
                placeholder={t('attributes.products.description.placeholder')}
                variant={variantTypes.OUTLINED}
                error={Boolean(errors && errors?.description)}
                helperText={(errors && errors?.description) || ''}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </div>
  )
})

AttributeProductForm.displayName = 'AttributeProductForm'

AttributeProductForm.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    weight: PropTypes.number,
    dimensions: PropTypes.string,
    scope: PropTypes.string
  }),
  errors: PropTypes.shape({
    name: PropTypes.any,
    description: PropTypes.any,
    quantity: PropTypes.any,
    weight: PropTypes.any,
    dimensions: PropTypes.any
  })
}

AttributeProductForm.defaultProps = {
  setOpenDialog: () => {},
  dispatchAction: () => {},
  title: '',
  product: defaultValue,
  errors: null
}
